/**
 * Which one step is live, for any state a packaging run can be in.
 *
 * The wizard has eight steps and every card decided its own availability from
 * its own slice of the facts. That is eight independent answers to one
 * question, and they disagreed: in STRUCTURING both take-stock and naming drew
 * as current; at GATE_PENDING the delivery control was offered even though no
 * worker claims a delivery until GATE_PASSED; and when the gate passed without
 * advancing the run, the check button stayed live over a press whose result
 * was already known.
 *
 * So the question is answered once, here, and every card reads from it. The
 * rule is: at most one step is live. Not one per section, not one per state
 * machine branch - one on the page.
 *
 * `null` is a real answer and an important one. A run that is delivering, or
 * delivered, or stalled in a way no press can clear, has no live step, and
 * saying so is better than leaving a control lit that cannot work.
 */
export type WizardStep = 'include' | 'layout' | 'stock' | 'name' | 'documents' | 'files' | 'gate' | 'deliver';

export interface RunFacts {
  /** The backend says a population is registered and dispositioned. */
  willShip: boolean;
  /** Every gate criterion passes on the most recent read. */
  gateAllPass: boolean;
  /** An export has been built, so there are files to hand over. */
  exportsBuilt: boolean;
}

/**
 * The one step to act on, or null when no press on the page can help.
 *
 * Deliberately total over the states the contract declares, including the two
 * failure states, because "no rung to climb" is exactly when a wizard is most
 * likely to leave a stale control lit.
 */
export function liveStep(state: string, f: RunFacts): WizardStep | null {
  switch (state) {
    case 'DISCOVERING':
      return 'include';
    case 'AXIS_PENDING':
      return 'layout';
    // Two steps share this state and only one of them is live at a time:
    // naming closes structuring for good, so it must not be offered until the
    // population that only structuring can accept is registered.
    case 'STRUCTURING':
      return f.willShip ? 'name' : 'stock';
    case 'NAMING':
      return 'name';
    case 'DOCUMENTING':
      return 'documents';
    case 'EXPORTABLE':
      return f.exportsBuilt ? 'gate' : 'files';
    case 'GATE_PENDING':
      // Always the gate, including when every criterion already passes.
      //
      // This briefly returned null there, on the reasoning that a press which
      // answers 200 and changes nothing is not worth offering. That was true
      // of one broken build and wrong as a rule: the press is what seals the
      // release, and on a package this size it writes about a million evidence
      // links, so it is a slow success rather than a no-op. Removing the
      // control left an operator whose run was otherwise complete with no way
      // to finish it the moment the backend was fixed.
      return 'gate';
    case 'GATE_FAILED':
      return 'gate';
    case 'GATE_PASSED':
      return 'deliver';
    // The work is the backend's now, and a delivery that ended - either way -
    // is not restarted from this run.
    case 'DELIVERING':
    case 'DELIVERED':
    case 'DELIVERY_FAILED':
      return null;
    default:
      return null;
  }
}

/** How a card should draw itself, given the step that is live. */
export function stepState(id: WizardStep, live: WizardStep | null, order: readonly WizardStep[]): 'now' | 'done' | 'waiting' {
  if (id === live) return 'now';
  if (live === null) return 'done';
  return order.indexOf(id) < order.indexOf(live) ? 'done' : 'waiting';
}
